import { useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";
import {
  DEFAULT_VIDEO_SETTINGS,
  STORED_AUDIO_VIDEO_SETTINGS_KEY,
} from "../constants";
import { AudioVideoSettings, BackgroundType } from "../types";
import { deserializerToEnsureObjectEquality } from "../utils";

export type SetStoredAudioVideoSettings = (
  updater: (prev: AudioVideoSettings) => AudioVideoSettings,
) => void;

/**
 * In order for changes to Reactively propagate, please use this hook for
 * writing settings. However, if you only need to _read_ settings ad-hoc, then
 * use `getStoredAudioVideoSettings`.
 */
export const useStoredAudioVideoSettings = (): {
  storedAudioVideoSettings: AudioVideoSettings;
  setStoredAudioVideoSettings: SetStoredAudioVideoSettings;
} => {
  const [storedAudioVideoSettings, setStoredAudioVideoSettings] =
    useLocalStorage(STORED_AUDIO_VIDEO_SETTINGS_KEY, DEFAULT_VIDEO_SETTINGS, {
      deserializer: deserializerToEnsureObjectEquality,
    });

  return useMemo(
    () => ({
      storedAudioVideoSettings: {
        ...storedAudioVideoSettings,
        backgroundType:
          storedAudioVideoSettings?.backgroundType ?? BackgroundType.NONE,
      } as AudioVideoSettings,
      setStoredAudioVideoSettings: setStoredAudioVideoSettings,
    }),
    [storedAudioVideoSettings, setStoredAudioVideoSettings],
  );
};
